import { cloneElement, isValidElement, useId, useState, type InputHTMLAttributes, type TextareaHTMLAttributes, type SelectHTMLAttributes, type ReactNode, type Ref, type ChangeEvent } from 'react'
import { cn } from '@/lib/cn'
import { HelpTip } from './HelpTip'

/**
 * Form controls. Field edge is `--input` (#78859b, 3.73:1): every control boundary must
 * clear 3:1 (WCAG 1.4.11, spec §3.3.2). Focus is an accent border plus a soft 3px halo.
 */
const BASE = [
  'block w-full rounded-md border border-input bg-card px-3 text-sm text-foreground shadow-control',
  'transition-[border-color,box-shadow] duration-fast ease-standard',
  'placeholder:text-content-placeholder',
  'hover:border-foreground/40',
  'focus:border-ring focus:outline-none focus:ring-[3px] focus:ring-ring/20',
  'disabled:cursor-not-allowed disabled:bg-surface-disabled disabled:text-content-disabled',
  'aria-[invalid=true]:border-destructive aria-[invalid=true]:focus:ring-destructive/20',
].join(' ')

export function Input({ ref, ...props }: InputHTMLAttributes<HTMLInputElement> & { ref?: Ref<HTMLInputElement> }) {
  return <input {...props} ref={ref} className={cn(BASE, 'h-9', props.className)} />
}

/**
 * Multi-line text. With `maxLength` it shows a live "n / max" counter under the box (E-UX), so
 * a long reason or description never gets silently cut off by the browser.
 */
export function Textarea({ ref, ...props }: TextareaHTMLAttributes<HTMLTextAreaElement> & { ref?: Ref<HTMLTextAreaElement> }) {
  const counterId = useId()
  const controlled = props.value !== undefined && props.value !== null
  const [typed, setTyped] = useState(() => String(props.defaultValue ?? '').length)
  const box = (
    <textarea
      {...props}
      ref={ref}
      aria-describedby={props.maxLength ? [props['aria-describedby'], counterId].filter(Boolean).join(' ') : props['aria-describedby']}
      onChange={(e: ChangeEvent<HTMLTextAreaElement>) => { if (!controlled) setTyped(e.target.value.length); props.onChange?.(e) }}
      className={cn(BASE, 'min-h-[88px] resize-y py-2 leading-relaxed', props.className)}
    />
  )
  if (!props.maxLength) return box
  const used = controlled ? String(props.value).length : typed
  const near = used >= props.maxLength * 0.9
  return (
    <span className="block">
      {box}
      <span id={counterId} className={cn('mt-1 block text-right text-[11px] tabular-nums', near ? 'font-medium text-warning-content' : 'text-muted-foreground')}>
        {used.toLocaleString('en-IN')} / {props.maxLength.toLocaleString('en-IN')}
      </span>
    </span>
  )
}

export function Select({ ref, ...props }: SelectHTMLAttributes<HTMLSelectElement> & { ref?: Ref<HTMLSelectElement> }) {
  return (
    <select
      {...props}
      ref={ref}
      className={cn(
        BASE,
        'h-9 cursor-pointer appearance-none bg-[length:16px] bg-[right_0.6rem_center] bg-no-repeat pr-9',
        // chevron drawn in the field's own tertiary colour — no extra DOM
        "bg-[url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%235d6b84' stroke-width='1.75' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='m6 9 6 6 6-6'/%3E%3C/svg%3E\")]",
        props.className,
      )}
    />
  )
}

interface FieldProps {
  label?: ReactNode
  required?: boolean
  /** Contextual help (e.g. what a statutory number is) shown from a "?" next to the label. */
  help?: ReactNode
  hint?: ReactNode
  error?: ReactNode
  children: ReactNode
  className?: string
}

const CONTROL_TAGS = new Set<unknown>(['input', 'select', 'textarea'])
/** Only real form controls take aria-required (axe: aria-allowed-attr). */
function isFormControl(type: unknown): boolean {
  if (CONTROL_TAGS.has(type)) return true
  // A marker property, not the function name (names are minified in production builds).
  return !!(type && (type as { formControl?: boolean }).formControl)
}

export function Field({ label, required, help, hint, error, children, className }: FieldProps) {
  const msgId = useId()
  // A single control child is marked invalid and pointed at its message, so the red edge
  // and the screen-reader announcement come from one `error` prop (spec 21 §3.9.3, 422).
  // `required` is announced through aria-required on the control (the red * is decorative).
  const single = isValidElement<{ 'aria-invalid'?: unknown; 'aria-describedby'?: string; 'aria-required'?: boolean }>(children)
  const control = single
    ? cloneElement(children, {
        ...(required && isFormControl(children.type) ? { 'aria-required': true } : {}),
        ...(error ? { 'aria-invalid': true, 'aria-describedby': msgId } : {}),
      })
    : children
  return (
    <label className={cn('block space-y-1.5', className)}>
      {label && (
        <span className="block text-[13px] font-medium text-foreground">
          {label}
          {required && <span className="ml-0.5 text-destructive" aria-hidden>*</span>}
          {help && <HelpTip>{help}</HelpTip>}
        </span>
      )}
      {control}
      {hint && !error && <span className="block text-xs text-muted-foreground">{hint}</span>}
      {error && <span id={msgId} role="alert" className="block text-xs font-medium text-danger-content">{error}</span>}
    </label>
  )
}

// Components that render a single form control (see isFormControl).
;(Input as unknown as { formControl: boolean }).formControl = true
;(Textarea as unknown as { formControl: boolean }).formControl = true
;(Select as unknown as { formControl: boolean }).formControl = true
